import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import { mainSiteUrl } from '@/mocks/site';
import { categories } from '@/mocks/categories';

type NavItem = {
  labelKey: string;
  type: 'route' | 'scroll';
  target: string;
  icon: string;
};

const navItems: NavItem[] = [
  { labelKey: 'nav_home', type: 'route', target: '/', icon: 'ri-home-line' },
  { labelKey: 'nav_spreadsheet', type: 'scroll', target: 'spreadsheet', icon: 'ri-table-line' },
  { labelKey: 'nav_guides', type: 'scroll', target: 'guides', icon: 'ri-book-open-line' },
  { labelKey: 'nav_shopping_guide', type: 'scroll', target: 'spreadsheet', icon: 'ri-shopping-bag-line' },
  { labelKey: 'nav_safety', type: 'scroll', target: 'safety', icon: 'ri-shield-check-line' },
  { labelKey: 'nav_faq', type: 'scroll', target: 'faq', icon: 'ri-question-line' },
];

export default function Navbar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catsOpen, setCatsOpen] = useState(false);

  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 24);
    }
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  function handleNav(item: NavItem) {
    setMobileOpen(false);
    setCatsOpen(false);

    if (item.type === 'route') {
      if (location.pathname === item.target) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        navigate(item.target);
      }
      return;
    }

    const scrollToSection = () => {
      const el = document.getElementById(item.target);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    if (location.pathname === '/') {
      scrollToSection();
    } else {
      navigate('/');
      setTimeout(scrollToSection, 150);
    }
  }

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'border-b border-background-200 bg-background-50/95 backdrop-blur-md'
          : 'border-b border-white/10 bg-foreground-950/50 backdrop-blur-md'
      }`}
    >
      <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 md:px-6">
        <Link
          to="/"
          className="flex items-center gap-2 font-heading text-xl font-bold tracking-tight"
        >
          <span
            className={`flex h-8 w-8 items-center justify-center rounded-lg ${
              scrolled ? 'bg-primary-500 text-white' : 'bg-white/20 text-white backdrop-blur'
            }`}
          >
            <i className="ri-table-line text-base"></i>
          </span>
          <span className={scrolled ? 'text-foreground-950' : 'text-white'}>
            {t('brand')}
          </span>
        </Link>

        <div className="hidden items-center gap-1 lg:flex">
          {navItems.map((item) => (
            <button
              key={item.labelKey}
              onClick={() => handleNav(item)}
              className={`whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-semibold transition-colors ${
                scrolled
                  ? 'text-foreground-700 hover:bg-background-100 hover:text-foreground-950'
                  : 'text-white/90 hover:bg-white/15 hover:text-white'
              }`}
            >
              <span className="flex items-center gap-1.5">
                <i className={`${item.icon} text-sm`}></i>
                {t(item.labelKey)}
              </span>
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <LanguageSwitcher light={!scrolled} />
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="hidden whitespace-nowrap rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600 md:inline-flex"
          >
            {t('explore_main')}
          </a>
          <button
            onClick={() => setMobileOpen((v) => !v)}
            className={`flex h-9 w-9 items-center justify-center rounded-lg lg:hidden ${
              scrolled ? 'text-foreground-950 hover:bg-background-100' : 'text-white hover:bg-white/15'
            }`}
            aria-label="Menu"
          >
            <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-xl`}></i>
          </button>
        </div>
      </nav>

      {mobileOpen && (
        <div className="max-h-[calc(100vh-4rem)] overflow-y-auto border-t border-background-200 bg-background-50 px-4 py-3 lg:hidden">
          <div className="flex flex-col gap-1">
            {navItems.map((item) => (
              <button
                key={item.labelKey}
                onClick={() => handleNav(item)}
                className="flex items-center gap-2 whitespace-nowrap rounded-lg px-3 py-2.5 text-left text-sm font-semibold text-foreground-800 hover:bg-background-100"
              >
                <i className={`${item.icon} text-base`}></i>
                {t(item.labelKey)}
              </button>
            ))}

            {/* Categories section in mobile menu */}
            <div className="mt-2 border-t border-background-200 pt-2">
              <button
                onClick={() => setCatsOpen((v) => !v)}
                className="flex w-full items-center justify-between gap-2 whitespace-nowrap rounded-lg px-3 py-2.5 text-sm font-semibold text-foreground-800 hover:bg-background-100"
              >
                <span className="flex items-center gap-2">
                  <i className="ri-apps-2-line text-base"></i>
                  {t('categories_title')}
                </span>
                <i className={`ri-arrow-down-s-line text-base transition-transform ${catsOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {catsOpen && (
                <div className="mt-1 grid grid-cols-1 gap-1 sm:grid-cols-2">
                  {categories.map((cat) => (
                    <a
                      key={cat.key}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => {
                        setMobileOpen(false);
                        setCatsOpen(false);
                      }}
                      className="flex items-center gap-2 whitespace-nowrap rounded-lg px-3 py-2 text-sm text-foreground-700 hover:bg-background-100"
                    >
                      <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center overflow-hidden rounded-md">
                        <img src={cat.image} alt={t(`cat_${cat.key}`)} className="h-full w-full object-cover object-top" />
                      </span>
                      <span className="truncate">{t(`cat_${cat.key}`)}</span>
                      <i className="ri-external-link-line ml-auto text-xs text-foreground-400"></i>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="mt-2 whitespace-nowrap rounded-lg bg-primary-500 px-3 py-2.5 text-center text-sm font-semibold text-white"
            >
              {t('explore_main')}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}